import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Spinner, ErrorBox, Success, DataTable, BudgetStatus, BudgetBar,
  fmtDate, money, num } from '../components/ui';

/**
 * Project expenditure. One entry can carry many line items, because that
 * is how the costs actually arrive — a stack of invoices recorded in one
 * sitting, each with its own date, description and amount.
 *
 * The three headline figures (budgeted, spent, balance) are computed by
 * the database from the line items themselves, so what is shown at the
 * top always adds up to what is listed below it.
 */
const today = () => new Date().toISOString().slice(0, 10);
const blankItem = () => ({ itemDate: today(), description: '', amount: '' });

export default function Expenditure() {
  const { id } = useParams();
  const { can, user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [entering, setEntering] = useState(false);
  const [head, setHead] = useState({ reference: '', note: '' });
  const [items, setItems] = useState([blankItem()]);
  const canCreate = can('expenditure.create');

  const load = useCallback(() =>
    api.get(`/projects/${id}/expenditure`).then(r => setData(r.data)).catch(setErr), [id]);
  useEffect(() => { setData(null); load(); }, [id, load]);

  const setItem = (i, k, v) => setItems(list => list.map((it, j) => j === i ? { ...it, [k]: v } : it));
  const addItem = () => setItems(list => [...list, blankItem()]);
  const removeItem = i => setItems(list => list.length > 1 ? list.filter((_, j) => j !== i) : list);
  const reset = () => { setItems([blankItem()]); setHead({ reference: '', note: '' }); setEntering(false); };

  const draftTotal = items.reduce((a, it) => a + (Number(it.amount) || 0), 0);

  const submit = async e => {
    e.preventDefault(); setErr(null);
    const filled = items.filter(it => String(it.description).trim() || it.amount !== '');
    if (!filled.length) return setErr(new Error('Add at least one item'));
    setBusy(true);
    try {
      const r = await api.post(`/projects/${id}/expenditure`, {
        reference: head.reference || null,
        note: head.note || null,
        items: filled.map(it => ({ itemDate: it.itemDate, description: it.description, amount: Number(it.amount) }))
      });
      setSaved(r.data.message);
      setTimeout(() => setSaved(null), 3500);
      reset(); load();
    } catch (e2) { setErr(e2); } finally { setBusy(false); }
  };

  const removeEntry = async entryId => {
    if (!window.confirm('Remove this expenditure entry and all of its items?')) return;
    try { await api.delete(`/projects/${id}/expenditure/${entryId}`); load(); }
    catch (e) { setErr(e); }
  };

  if (err && !data) return <ErrorBox error={err} />;
  if (!data) return <Spinner label="Loading budget…" />;

  const b = data.budget || {};
  const overspend = Number(b.balance) < 0;
  // What the balance would become if the draft below were saved.
  const projected = Number(b.balance || 0) - draftTotal;

  return <>
    <div className="phead">
      <div>
        <h1 style={{ marginBottom: 2 }}>Budget &amp; expenditure</h1>
        <div className="muted">
          <Link to={`/projects/${id}`}>{data.project.name}</Link>
          {' · '}<Link to={`/programmes/${data.project.programme_id}`}>{data.project.programme_name}</Link>
          {' · '}{data.project.reference}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => window.print()}>Print / PDF</button>
        {canCreate && !entering &&
          <button className="primary" onClick={() => setEntering(true)}>+ Record expenditure</button>}
      </div>
    </div>

    <Success message={saved} />
    <ErrorBox error={err} />

    {/* The three figures the client asked to see above the entries. */}
    <div className="stat-grid">
      <div className="stat static"><div className="label">Budgeted amount</div>
        <div className="value">{money(b.budget_amount)}</div>
        <div className="sub">overall project budget</div></div>
      <div className="stat static soon"><div className="label">Total spent</div>
        <div className="value">{money(b.total_spent)}</div>
        <div className="sub">{num(b.item_count)} items across {num(b.entry_count)} entries</div></div>
      <div className={`stat static ${overspend ? 'over' : 'complete'}`}><div className="label">Balance</div>
        <div className={`value ${overspend ? 'neg' : ''}`}>{money(b.balance)}</div>
        <div className="sub">{overspend ? 'over the budget' : 'left to spend'}</div></div>
      <div className="stat static"><div className="label">Budget status</div>
        <div className="value" style={{ fontSize: 19, paddingTop: 4 }}><BudgetStatus value={b.budget_status} /></div>
        <div className="sub"><BudgetBar pct={b.pct_spent} status={b.budget_status} /></div></div>
    </div>

    {b.budget_status === 'No budget set' && (
      <div className="alert info">
        No budget has been set for this project, so spend cannot be tracked against anything.
        The budgeted amount is set when the project is created.
      </div>
    )}
    {b.budget_status === 'Over budget' && (
      <div className="alert error">
        <strong>Over budget.</strong> Spend of {money(b.total_spent)} exceeds the
        {' '}{money(b.budget_amount)} budget by {money(Math.abs(Number(b.balance)))}.
      </div>
    )}
    {b.budget_status === 'Approaching limit' && (
      <div className="alert warn">
        <strong>Approaching the budget limit.</strong> {Number(b.pct_spent).toFixed(0)}% of the
        {' '}{money(b.budget_amount)} budget has been spent, leaving {money(b.balance)}.
      </div>
    )}

    {/* ---- entry form: one expenditure, many items ---- */}
    {canCreate && entering && (
      <div className="panel no-print">
        <h2>Record expenditure</h2>
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
            <div className="field"><label>Reference (optional)</label>
              <input style={{ width: '100%' }} value={head.reference}
                onChange={e => setHead({ ...head, reference: e.target.value })} placeholder="Invoice or batch reference" /></div>
            <div className="field"><label>Note (optional)</label>
              <input style={{ width: '100%' }} value={head.note}
                onChange={e => setHead({ ...head, note: e.target.value })} placeholder="What this batch covers" /></div>
          </div>

          <div className="phead" style={{ marginTop: 4 }}>
            <h3 style={{ margin: 0 }}>Items</h3>
            <button type="button" onClick={addItem}>+ Add item</button>
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead><tr>
                <th style={{ width: '18%' }}>Date</th>
                <th style={{ width: '48%' }}>Description of the item</th>
                <th style={{ width: '20%' }}>Amount</th>
                <th style={{ width: '14%' }}>Created by</th>
                <th></th>
              </tr></thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i}>
                    <td><input type="date" style={{ width: '100%' }} value={it.itemDate}
                      onChange={e => setItem(i, 'itemDate', e.target.value)} /></td>
                    <td><input style={{ width: '100%' }} value={it.description}
                      onChange={e => setItem(i, 'description', e.target.value)} placeholder="What was bought or paid for" /></td>
                    <td><input type="number" step="0.01" min="0" style={{ width: '100%' }} value={it.amount}
                      onChange={e => setItem(i, 'amount', e.target.value)} placeholder="0.00" /></td>
                    <td className="muted">{user?.fullName}</td>
                    <td><button type="button" onClick={() => removeItem(i)} disabled={items.length === 1}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr>
                <th>Total</th><th></th>
                <th>{money(draftTotal)}</th><th colSpan={2}></th>
              </tr></tfoot>
            </table>
          </div>

          {b.budget_amount != null && draftTotal > 0 && (
            <div className={`alert ${projected < 0 ? 'error' : 'info'}`} style={{ marginTop: 12 }}>
              {projected < 0
                ? <>Saving this would take the project {money(Math.abs(projected))} <strong>over</strong> its {money(b.budget_amount)} budget.</>
                : <>Saving this would leave a balance of <strong>{money(projected)}</strong> against the {money(b.budget_amount)} budget.</>}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="primary" disabled={busy}>{busy ? 'Saving…' : `Save ${items.length} item${items.length > 1 ? 's' : ''}`}</button>
            <button type="button" onClick={reset}>Cancel</button>
            <span className="muted" style={{ alignSelf: 'center' }}>Your name is recorded against each item automatically.</span>
          </div>
        </form>
      </div>
    )}

    {/* ---- every entry, with its items ---- */}
    <div className="panel">
      <div className="phead"><h2 style={{ margin: 0 }}>Expenditure entries</h2>
        <span className="muted">{num(b.entry_count)} entries · {num(b.item_count)} items · {money(b.total_spent)}</span></div>

      {data.entries.length === 0
        ? <p className="muted">Nothing has been recorded against this project yet.</p>
        : data.entries.map(e => (
          <div key={e.id} className="entry-block">
            <div className="phead" style={{ marginBottom: 6 }}>
              <div>
                <strong>{e.reference || 'Expenditure'}</strong>
                <span className="muted"> · {num(e.item_count)} item{Number(e.item_count) === 1 ? '' : 's'} · entered by {e.created_by_name || 'Unknown'} on {fmtDate(e.created_at)}</span>
                {e.note ? <div className="muted">{e.note}</div> : null}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <strong>{money(e.entry_total)}</strong>
                {canCreate && <button className="no-print" onClick={() => removeEntry(e.id)}>Remove</button>}
              </div>
            </div>
            <DataTable rows={e.items} columns={[
              { key: 'item_date', label: 'Date', render: fmtDate },
              { key: 'description', label: 'Description of the item' },
              { key: 'amount', label: 'Amount', render: money },
              { key: 'created_by_name', label: 'Created by', render: v => v || '—' }
            ]} />
          </div>
        ))}
    </div>
  </>;
}
